// ** React Imports
import React, { Dispatch, SetStateAction } from 'react'

// ** MUI Imports
import {
  Divider,
  Typography,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  ButtonPropsColorOverrides,
  IconButton
} from '@mui/material'
import { useTheme } from '@emotion/react'
import { Close } from 'mdi-material-ui'
import { LoadingButton } from '@mui/lab'
import { useUser } from 'src/lib/contexts/UserContext'

interface ConfirmationProps {
  isOpen: boolean
  setIsConfirmationOpen: (open: boolean) => void | Dispatch<SetStateAction<boolean>>
  handleJob: () => Promise<void> | void
  cancelButtonJob?: () => Promise<void> | void
  jobButton: string
  cancelButton: string
  confirmationText: React.ReactNode
  referrer?: string
  title: string
  jobButtonColor: any
  cancelButtonColor: any
  isLoading?: boolean
  skipCloseOnSave?: boolean
}

const ConfirmationDialog: React.FC<ConfirmationProps> = ({
  isOpen,
  setIsConfirmationOpen,
  handleJob,
  cancelButtonJob,
  jobButton,
  cancelButton,
  confirmationText,
  referrer = '',
  title,
  jobButtonColor,
  cancelButtonColor,
  isLoading,
  skipCloseOnSave = false
}) => {
  const { user } = useUser()
  const doTheJob = async () => {
    await handleJob()
    if (Boolean(skipCloseOnSave)) return
    setIsConfirmationOpen(false)
  }
  const theme: any = useTheme()

  return (
    <Dialog
      open={isOpen}
      onClose={() => setIsConfirmationOpen(false)}
      PaperProps={{ sx: { borderRadius: 4, gap: 2, maxWidth: 540 } }}
    >
      <DialogTitle
        sx={{
          padding: '1rem 1rem 0.5rem 1rem',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          fontWeight: 600,
          color: `${theme.palette.text.header}`,
          fontSize: '16px !important'
        }}
      >
        {title}
        <IconButton
          aria-label='close'
          onClick={() => {
            setIsConfirmationOpen(false)
          }}
        >
          <Close color={'secondary'} sx={{ transform: 'scale(1)' }} />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <div>
          <Typography variant='body1' sx={{ fontWeight: 400 }}>
            {confirmationText}
          </Typography>
        </div>
      </DialogContent>
      <DialogActions sx={{ display: 'flex', justifyContent: 'end' }}>
        <Button
          id={`${referrer}${referrer != '' ? '-' : ''}cancel-button`}
          onClick={() => (cancelButtonJob ? cancelButtonJob() : setIsConfirmationOpen(false))}
          variant='text'
          size='large'
          color={cancelButtonColor}
          sx={{ maxWidth: 240 }}
        >
          {cancelButton}
        </Button>
        <LoadingButton
          disabled={user?.isDemoUser}
          id={`${referrer}${referrer != '' ? '-' : ''}job-button`}
          onClick={doTheJob}
          size='medium'
          loadingPosition='end'
          loading={isLoading}
          variant='contained'
          color={jobButtonColor}
          sx={{
            fontSize: 16,
            maxWidth: 200,
            outline: 'none'
          }}
        >
          {jobButton}
        </LoadingButton>
      </DialogActions>
    </Dialog>
  )
}

export default ConfirmationDialog
